import { useState, useContext } from 'react';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, useFlowFormGlobal } from 't-lib-ui/structure';
import { TextButton, TextArea } from 't-lib-ui/widgets';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { AppState } from '../../context/general';
import { nonNull } from 't-lib-ui/helpers';
import { requestFeed, subscribeWithMode, unsubscribeFeed } from '../../connector/api';
import { RestUserFeed } from '../../schema-copy/api-schema';

type ImportStatus = 'pending' | 'queued' | 'polling' | 'exists' | 'error';

interface FeedImportResult {
  url: string;
  status: ImportStatus;
  message?: string;
}

const STATUS_COLOR: Record<ImportStatus, string> = {
  pending: 'var(--gray-500)',
  queued: 'var(--dr-link-blue)',
  polling: 'var(--gray-500)',
  exists: 'var(--success-color)',
  error: 'var(--error-color)',
};

const STATUS_LABEL: Record<ImportStatus, string> = {
  pending: 'pending',
  queued: 'queued',
  polling: 'checking',
  exists: 'ready',
  error: 'error',
};

const AdminFeedImportPage: React.FC = () => {
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [urlsText, setUrlsText] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<FeedImportResult[]>([]);
  const appState = useContext(AppState);

  useFlowFormGlobal();

  const extractUrls = (text: string): string[] =>
    text
      .split('\n')
      .map(line => {
        const t = line.trim();
        if (!t) return '';
        if (t.startsWith('http://') || t.startsWith('https://')) return t;
        return 'https://' + t;
      })
      .filter(line => line.startsWith('http'));

  const TRANSIENT = new Set(['FETCHING']);
  const POLL_DELAYS = [3000, 3000, 4000, 4000, 5000, 5000, 5000, 5000, 5000, 5000];

  const updateResult = (r: FeedImportResult) =>
    setResults(prev => prev.map(x => x.url == r.url ? r : x));

  const resolveResult = (url: string, result: any): FeedImportResult | 'subscribe' => {
    if ('id' in result)
      return { url, status: 'exists', message: (result as RestUserFeed).title || undefined };
    const feedStatus = (result as any).feedStatus as string;
    if (feedStatus == 'READY') return 'subscribe'; // READY feed needs to be created as public
    const isBad = feedStatus == 'BAD' || feedStatus == 'BLOCK';
    return { url, status: isBad ? 'error' : 'queued', message: feedStatus };
  };

  const handleImport = async () => {
    if (importing) return;

    // Skip URLs already processed (not stuck-pending); allow retry of pending entries
    const processedUrls = new Set(results.filter(r => r.status != 'pending').map(r => r.url));
    const urlsToImport = extractUrls(urlsText).filter(url => !processedUrls.has(url));
    if (!urlsToImport.length) return;

    setImporting(true);

    // Remove stale pending entries for these URLs, then append fresh ones
    const importSet = new Set(urlsToImport);
    setResults(prev => [
      ...prev.filter(r => !(r.status == 'pending' && importSet.has(r.url))),
      ...urlsToImport.map(url => ({ url, status: 'pending' as ImportStatus })),
    ]);

    const auth = nonNull(appState.state.authData);

    // Phase 1: fire all initial requests sequentially
    const toPoll: string[] = [];

    for (const url of urlsToImport) {
      try {
        const result = await requestFeed(auth, url);
        const feedResult = resolveResult(url, result);
        if (feedResult == 'subscribe') {
          // READY feed — create as public, but don't keep admin subscribed
          const subResult = await subscribeWithMode(auth, url, 'public');
          await unsubscribeFeed(auth, subResult.feed.id);
          updateResult({ url, status: 'exists', message: subResult.feed.title || undefined });
        } else {
          updateResult(feedResult);
          if (feedResult.status == 'queued' && TRANSIENT.has(feedResult.message ?? ''))
            toPoll.push(url);
        }
      } catch (e) {
        updateResult({ url, status: 'error', message: (e as Error).message });
      }
    }

    // Phase 2: poll transient feeds in parallel until they resolve
    if (toPoll.length) {
      await Promise.all(toPoll.map(async (url: string) => {
        updateResult({ url, status: 'polling', message: 'checking...' });
        for (const delay of POLL_DELAYS) {
          await new Promise(r => setTimeout(r, delay));
          try {
            const poll = await requestFeed(auth, url);
            const feedResult = resolveResult(url, poll);
            if (feedResult == 'subscribe') {
              const subResult = await subscribeWithMode(auth, url, 'public');
              await unsubscribeFeed(auth, subResult.feed.id);
              updateResult({ url, status: 'exists', message: subResult.feed.title || undefined });
              return;
            }
            updateResult(feedResult);
            if (feedResult.status != 'queued' || !TRANSIENT.has(feedResult.message ?? ''))
              return; // reached a terminal state
          } catch (e) {
            updateResult({ url, status: 'error', message: (e as Error).message });
            return;
          }
        }
        // Still transient after all polls — leave as queued
        updateResult({ url, status: 'queued', message: 'still processing' });
      }));
    }

    setImporting(false);
  };

  const processedUrls = new Set(results.filter(r => r.status != 'pending').map(r => r.url));
  const urlCount = extractUrls(urlsText).filter(url => !processedUrls.has(url)).length;
  const doneCount = results.filter(r => r.status != 'pending').length;

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
          <MainLayout
            selectedFeed="admin-feeds"
            right={({ onExpand }) => (
              <FlowForm main={true} className='full-margin'>
                <style>{`
                  .feed-import-container {
                    max-width: 700px;
                    padding: 12px 24px;
                  }
                  .feed-import-container h2 {
                    margin: 0 0 1rem 0;
                    font-size: 1.5rem;
                    font-weight: 600;
                  }
                  .feed-import-container .labelledTextareaContainer {
                    width: 100%;
                  }
                  .feed-import-container .labelledTextareaContainer textarea {
                    width: 100%;
                    box-sizing: border-box;
                  }
                  .feed-import-actions {
                    margin: 0.75rem 0;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                  }
                  .feed-import-summary {
                    font-size: 0.9em;
                    color: var(--gray-400);
                  }
                  .feed-import-results {
                    margin-top: 0.5rem;
                    width: 100%;
                  }
                  .feed-import-row {
                    display: flex;
                    gap: 10px;
                    padding: 5px 0;
                    align-items: baseline;
                    border-bottom: 1px solid var(--gray-950);
                  }
                  .feed-import-status {
                    font-weight: 600;
                    min-width: 60px;
                    font-size: 0.85em;
                    flex-shrink: 0;
                  }
                  .feed-import-url {
                    flex: 1;
                    font-size: 0.85em;
                    word-break: break-all;
                  }
                  .feed-import-msg {
                    font-size: 0.8em;
                    color: var(--gray-500);
                    white-space: nowrap;
                    flex-shrink: 0;
                  }
                `}</style>
                <div className="feed-import-container">
                  <AdminHeader title="Import Feeds" onExpand={onExpand} />
                  <TextArea
                    label="Feed URLs (one per line, http/https only)"
                    value={urlsText}
                    onInput={v => setUrlsText(v.split('\n').map(l => l.trim()).join('\n'))}
                    rows={12}
                    placeholder={'https://example.com/feed.xml\nhttps://another.com/rss'}
                    layout="singleCol"
                  />
                  <div className="feed-import-actions">
                    <TextButton
                      label={importing ? `Importing ${doneCount}/${results.length}...` : `Import ${urlCount} Feed${urlCount == 1 ? '' : 's'}`}
                      type="major"
                      onClick={handleImport}
                      focussable="flow"
                    />
                    {!importing && results.length > 0 && (
                      <TextButton
                        label="Clear"
                        type="minor"
                        onClick={() => setResults([])}
                        focussable="flow"
                      />
                    )}
                    {!importing && results.length > 0 && (
                      <span className="feed-import-summary">
                        {results.filter(r => r.status == 'queued').length} queued,{' '}
                        {results.filter(r => r.status == 'exists').length} ready,{' '}
                        {results.filter(r => r.status == 'error').length} failed
                      </span>
                    )}
                  </div>
                  {results.length > 0 && (
                    <div className="feed-import-results">
                      {results.map((r, i) => (
                        <div key={i} className="feed-import-row">
                          <span className="feed-import-status" style={{ color: STATUS_COLOR[r.status] }}>
                            {STATUS_LABEL[r.status]}
                          </span>
                          <span className="feed-import-url" style={{ color: r.status == 'error' ? 'var(--error-color)' : 'var(--gray-200)' }}>
                            {r.url}
                          </span>
                          {r.message && <span className="feed-import-msg">{r.message}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </FlowForm>
            )}
            startWithLeftCollapsed={false}
          />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminFeedImportPage;
